import Notification from 'components/notification/Notification';
import PageHeader from 'components/common/PageHeader';
import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Form, Modal, Row, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import {
  fetchPushPreferences,
  fetchNotificationsFeed,
  markNotificationsRead,
  subscribeIncomingNotifications,
  syncNotifications,
  updatePushPreferences,
} from 'helpers/notifications';

const CATEGORY_KEYS = [
  'mercadolibre',
  'mercadolibre_communications',
  'internal',
  'sales',
  'questions'
];

const DEFAULT_PUSH_PREFERENCES = {
  categories: {},
  agent_headsup_enabled: true,
  mercadolibre_topics: {}
};

const DEFAULT_PUSH_CATALOG = {
  categories: [],
  mercadolibre_topics: [],
  agent_headsup: null
};

const DEFAULT_PAGINATION = {
  page: 1,
  page_size: 20,
  total_items: 0,
  total_pages: 0,
  has_prev: false,
  has_next: false,
};

const humanizeToken = value => {
  const normalized = String(value || '').trim().replace(/[._-]+/g, ' ');
  if (!normalized) return '';
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
};

const normalizeTopicTranslationKey = topic =>
  String(topic || '').trim().toUpperCase().replace(/[.-]/g, '_');

const extractIdFromTitle = (title, suffixToken) => {
  const source = String(title || '');
  const matcher = new RegExp(`${suffixToken}\\s*#?([A-Za-z0-9-]+)`, 'i');
  const match = source.match(matcher);
  return match && match[1] ? String(match[1]) : '';
};

const extractItemIdFromResource = resource => {
  const normalized = String(resource || '').trim();
  if (!normalized) return '';

  const patterns = [
    /\/items\/(ML[A-Z0-9]+)/i,
    /\/marketplace\/benchmarks\/items\/(ML[A-Z0-9]+)/i,
    /\/user-products\/(ML[A-Z0-9-]+)/i,
    /\b(ML[A-Z0-9]+)\b/i,
  ];

  for (const pattern of patterns) {
    const match = normalized.match(pattern);
    if (match && match[1]) {
      return String(match[1]);
    }
  }
  return '';
};

const formatAmount = (value, locale) => {
  if (value === null || value === undefined || value === '') return '';
  const numeric = Number(value);
  if (Number.isNaN(numeric)) return String(value);
  try {
    return new Intl.NumberFormat(locale || undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(numeric);
  } catch (error) {
    return String(value);
  }
};

const localizeStatus = (status, t) => {
  const normalized = String(status || '').trim().toUpperCase().replace(/[.-]/g, '_');
  if (!normalized) return '';
  return t(`NOTIFICATIONS.STATUS.${normalized}`, {
    defaultValue: humanizeToken(status)
  });
};

const localizeTopic = (topic, t) => {
  const normalized = normalizeTopicTranslationKey(topic);
  if (!normalized) return '';
  return t(`NOTIFICATIONS.TOPICS.${normalized}`, {
    defaultValue: humanizeToken(topic)
  });
};

const getTopicIcon = topicKey => {
  const topic = String(topicKey || '').toLowerCase();
  if (topic.startsWith('shipments')) return 'truck';
  if (topic.startsWith('payments')) return 'money-check-alt';
  if (topic.startsWith('orders')) return 'shopping-cart';
  if (topic.startsWith('questions')) return 'question-circle';
  if (topic.includes('claims')) return 'exclamation-triangle';
  if (topic.includes('price') || topic.includes('offers') || topic.includes('candidates')) return 'tag';
  if (topic.startsWith('items') || topic.includes('stock')) return 'box-open';
  if (topic.includes('communications')) return 'comments';
  return 'bell';
};

const resolveNotificationDestination = notification => {
  const metadata = notification.metadata || {};
  const topic = String(notification.topic || metadata.topic_key || metadata.topic || '').toLowerCase();
  const link = String(notification.link || '').trim();

  const hasInternalLink = link && link !== '#!' && !/^https?:\/\//i.test(link);
  if (hasInternalLink && link !== '/social/notifications') {
    return link;
  }

  const resourceItemId = extractItemIdFromResource(notification.resource || metadata.resource);
  const itemId = metadata.item_id || resourceItemId;
  if (itemId) {
    return `/posts/details/${itemId}`;
  }

  if (metadata.question_id) {
    return `/questions/${metadata.question_id}`;
  }

  if (metadata.claim_id || topic.includes('claims') || topic.startsWith('post_purchase')) {
    return '/claims';
  }

  if (metadata.order_id || topic.startsWith('orders') || topic.startsWith('payments') || topic.startsWith('shipments')) {
    return '/sales';
  }

  if (topic.startsWith('messages')) {
    return '/mercadolibre-messages';
  }

  return '';
};

const shouldAutoNavigate = destination => {
  if (!destination) return false;
  return [
    '/posts/details/',
    '/sales',
    '/claims',
    '/questions/',
    '/mercadolibre-messages',
    '/messages',
  ].some(prefix => destination.startsWith(prefix));
};

const getDisplayNotification = (notification, t, locale) => {
  const metadata = notification.metadata || {};
  const topic = String(notification.topic || metadata.topic_key || metadata.topic || '').toLowerCase();
  const status = metadata.status || '';

  const shipmentId = metadata.shipment_id || extractIdFromTitle(notification.title, 'shipment');
  if (topic === 'shipments' || shipmentId) {
    return {
      ...notification,
      displayTitle: t('NOTIFICATIONS.TITLES.SHIPMENT', { id: shipmentId || '-' }),
      displayMessage: localizeStatus(status || notification.message, t),
      displayTopic: localizeTopic('shipments', t),
      destination: resolveNotificationDestination(notification),
      avatar: { icon: getTopicIcon('shipments'), size: 'xl' }
    };
  }

  const paymentId = metadata.payment_id || extractIdFromTitle(notification.title, 'payment');
  if (topic === 'payments' || paymentId) {
    return {
      ...notification,
      displayTitle: t('NOTIFICATIONS.TITLES.PAYMENT', { id: paymentId || '-' }),
      displayMessage: localizeStatus(status || notification.message, t),
      displayTopic: localizeTopic('payments', t),
      destination: resolveNotificationDestination(notification),
      avatar: { icon: getTopicIcon('payments'), size: 'xl' }
    };
  }

  const orderId = metadata.order_id || extractIdFromTitle(notification.title, 'order');
  if (topic === 'orders' || topic === 'orders_v2' || topic === 'created_orders' || orderId) {
    const localizedStatus = localizeStatus(status, t);
    const total = formatAmount(metadata.total_amount, locale);
    const message = localizedStatus || total
      ? t('NOTIFICATIONS.MESSAGES.ORDER_STATUS_TOTAL', {
          status: localizedStatus || t('NOTIFICATIONS.NO_DATA'),
          total: total || t('NOTIFICATIONS.NO_DATA')
        })
      : (notification.message || t('NOTIFICATIONS.MESSAGES.ML_GENERIC_EVENT'));
    return {
      ...notification,
      displayTitle: t('NOTIFICATIONS.TITLES.ORDER', { id: orderId || '-' }),
      displayMessage: message,
      displayTopic: localizeTopic(topic || 'orders_v2', t),
      destination: resolveNotificationDestination(notification),
      avatar: { icon: getTopicIcon(topic || 'orders'), size: 'xl' }
    };
  }

  const resourceItemId = extractItemIdFromResource(notification.resource || metadata.resource);
  const itemId = metadata.item_id || resourceItemId || extractIdFromTitle(notification.title, 'item update');
  if (topic === 'items' || topic === 'items_prices' || topic === 'price_suggestion' || topic === 'stock_locations' || topic === 'stock_location' || itemId) {
    const fallbackMessage = topic === 'price_suggestion' || topic === 'items_prices'
      ? t('NOTIFICATIONS.MESSAGES.PRICE_UPDATE')
      : t('NOTIFICATIONS.MESSAGES.ITEM_UPDATE');
    const image = metadata.publication_image_url || '';
    return {
      ...notification,
      displayTitle: itemId
        ? t('NOTIFICATIONS.TITLES.ITEM_UPDATE', { id: itemId })
        : (notification.title || t('NOTIFICATIONS.TITLES.ITEM_GENERIC')),
      displayMessage: notification.message || fallbackMessage,
      displayTopic: localizeTopic(topic || 'items', t),
      destination: resolveNotificationDestination({ ...notification, metadata: { ...metadata, item_id: itemId } }),
      avatar: image
        ? { src: image, size: 'xl', rounded: 'soft' }
        : { icon: getTopicIcon(topic || 'items'), size: 'xl' }
    };
  }

  if (topic === 'public_candidates' || topic === 'public_offers' || topic === 'best_price_eligible') {
    return {
      ...notification,
      displayTitle: notification.title || t('NOTIFICATIONS.TITLES.PROMOTION_EVENT'),
      displayMessage: notification.message || t('NOTIFICATIONS.MESSAGES.ML_GENERIC_EVENT'),
      displayTopic: localizeTopic(topic, t),
      destination: resolveNotificationDestination(notification),
      avatar: { icon: getTopicIcon(topic), size: 'xl' }
    };
  }

  const image = metadata.publication_image_url || '';
  return {
    ...notification,
    displayTitle: notification.title || t('NOTIFICATIONS.TITLES.GENERIC'),
    displayMessage: notification.message || t('NOTIFICATIONS.MESSAGES.ML_GENERIC_EVENT'),
    displayTopic: localizeTopic(topic, t),
    destination: resolveNotificationDestination(notification),
    avatar: image
      ? { src: image, size: 'xl', rounded: 'soft' }
      : { icon: getTopicIcon(topic), size: 'xl' }
  };
};

const formatDateTime = (value, locale, fallback = '') => {
  if (!value) return fallback;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString(locale || undefined);
};

const escapeHtml = value =>
  String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const buildNotificationMarkup = notification => {
  const title = escapeHtml(notification.displayTitle || notification.title || '');
  const message = escapeHtml(notification.displayMessage || notification.message || '');
  const metadata = notification.metadata || {};
  const actions = Array.isArray(metadata.actions) ? metadata.actions : [];
  const categoryName = escapeHtml(metadata.category_name || notification.displayTopic || '');
  const subCategory = escapeHtml(metadata.sub_category || '');
  const highlighted = Boolean(metadata.highlighted);
  const tags = Array.isArray(metadata.tags) ? metadata.tags.filter(Boolean) : [];

  const metaParts = [categoryName, subCategory].filter(Boolean);
  const metaMarkup = metaParts.length
    ? `<div class="text-primary fs--2 mt-1">${metaParts.join(' · ')}</div>`
    : '';

  const tagsMarkup = tags.length
    ? `<div class="fs--2 mt-2 d-flex flex-wrap gap-1">${tags
        .slice(0, 4)
        .map(tag => `<span class="badge badge-soft-info me-1">${escapeHtml(tag)}</span>`)
        .join('')}</div>`
    : '';

  const highlightedMarkup = highlighted
    ? '<div class="fs--2 mt-1 text-warning"><strong>Destacado</strong></div>'
    : '';

  const actionsMarkup = actions.length
    ? `<div class="fs--2 mt-2">${actions
        .map(action => {
          const text = escapeHtml(action?.text || '');
          if (!text) return '';
          return `<span class="badge badge-soft-secondary me-1">${text}</span>`;
        })
        .filter(Boolean)
        .join('')}</div>`
    : '';

  const baseMarkup = `<strong>${title}</strong>`;
  if (!message) {
    return `${baseMarkup}${highlightedMarkup}${metaMarkup}${tagsMarkup}${actionsMarkup}`;
  }
  return `${baseMarkup}<div class="text-600 mt-1">${message}</div>${highlightedMarkup}${metaMarkup}${tagsMarkup}${actionsMarkup}`;
};

const buildPaginationArray = (page, totalPages) => {
  if (!totalPages) return [];
  const pages = [];
  const start = Math.max(1, page - 2);
  const end = Math.min(totalPages, page + 2);
  for (let value = start; value <= end; value += 1) {
    pages.push(value);
  }
  return pages;
};

const getNotificationTopicKey = notification =>
  String(
    notification?.topic ||
    notification?.metadata?.topic_key ||
    notification?.metadata?.topic ||
    ''
  )
    .trim()
    .toLowerCase();

const getNotificationStateKey = notification => {
  if (notification?.unread) return 'unread';
  if (notification?.metadata?.highlighted) return 'highlighted';
  return 'read';
};

const Notifications = () => {
  const { t, i18n } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [notifications, setNotifications] = useState([]);
  const [allowedCategories, setAllowedCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [unreadCount, setUnreadCount] = useState(0);
  const [error, setError] = useState('');
  const [meta, setMeta] = useState({});

  const [pushPreferences, setPushPreferences] = useState(DEFAULT_PUSH_PREFERENCES);
  const [pushCatalog, setPushCatalog] = useState(DEFAULT_PUSH_CATALOG);
  const [savingPushPreferences, setSavingPushPreferences] = useState(false);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [pagination, setPagination] = useState(DEFAULT_PAGINATION);
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedTopics, setSelectedTopics] = useState([]);

  const [selectedNotification, setSelectedNotification] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);

  const visibleCategories = useMemo(
    () => CATEGORY_KEYS.filter(category => allowedCategories.includes(category)),
    [allowedCategories]
  );

  const parsedNotifications = useMemo(
    () => (notifications || []).map(item => getDisplayNotification(item, t, i18n.language)),
    [notifications, t, i18n.language]
  );

  const localizePushCategoryLabel = option => {
    const key = String(option?.key || '').toUpperCase();
    if (!key) return '';
    return t(`NOTIFICATIONS.PUSH_FILTERS.CATEGORY.${key}`, {
      defaultValue: t(`NOTIFICATIONS.CATEGORIES.${key}`, {
        defaultValue: humanizeToken(option?.label || option?.key)
      })
    });
  };

  const localizePushTopicLabel = topicOption => {
    const key = normalizeTopicTranslationKey(topicOption?.key);
    if (!key) return humanizeToken(topicOption?.label || topicOption?.key);
    return t(`NOTIFICATIONS.PUSH_FILTERS.TOPIC.${key}`, {
      defaultValue: humanizeToken(topicOption?.label || topicOption?.key)
    });
  };

  const topicOptions = useMemo(() => {
    const known = new Set();
    const options = [];

    (pushCatalog.mercadolibre_topics || []).forEach(topicOption => {
      const key = String(topicOption?.key || '').trim().toLowerCase();
      if (!key || known.has(key)) return;
      known.add(key);
      options.push({
        key,
        label: localizePushTopicLabel(topicOption)
      });
    });

    parsedNotifications.forEach(notification => {
      const key = getNotificationTopicKey(notification);
      if (!key || known.has(key)) return;
      known.add(key);
      options.push({
        key,
        label: localizeTopic(key, t)
      });
    });

    return options.sort((left, right) => String(left.label || '').localeCompare(String(right.label || '')));
  }, [parsedNotifications, pushCatalog.mercadolibre_topics, t]);

  const filteredNotifications = useMemo(
    () => parsedNotifications.filter(notification => {
      if (selectedTopics.length) {
        const topicKey = getNotificationTopicKey(notification);
        if (!selectedTopics.includes(topicKey)) {
          return false;
        }
      }

      if (statusFilter !== 'all') {
        const statusKey = getNotificationStateKey(notification);
        if (statusFilter !== statusKey) {
          return false;
        }
      }

      return true;
    }),
    [parsedNotifications, selectedTopics, statusFilter]
  );

  const statusCounts = useMemo(() => {
    const counts = {
      all: parsedNotifications.length,
      unread: 0,
      read: 0,
      highlighted: 0
    };

    parsedNotifications.forEach(notification => {
      const key = getNotificationStateKey(notification);
      if (key === 'unread') {
        counts.unread += 1;
      }
      if (key === 'read') {
        counts.read += 1;
      }
      if (key === 'highlighted') {
        counts.highlighted += 1;
      }
    });

    return counts;
  }, [parsedNotifications]);

  const topicCounts = useMemo(() => {
    const counts = {};
    parsedNotifications.forEach(notification => {
      const key = getNotificationTopicKey(notification);
      if (!key) {
        return;
      }
      counts[key] = Number(counts[key] || 0) + 1;
    });
    return counts;
  }, [parsedNotifications]);

  const statusTabs = useMemo(() => [
    { key: 'all', label: t('NOTIFICATIONS.FILTERS.STATES.ALL'), variant: 'secondary' },
    { key: 'unread', label: t('NOTIFICATIONS.FILTERS.STATES.UNREAD'), variant: 'primary' },
    { key: 'read', label: t('NOTIFICATIONS.FILTERS.STATES.READ'), variant: 'success' },
    { key: 'highlighted', label: t('NOTIFICATIONS.FILTERS.STATES.HIGHLIGHTED'), variant: 'warning' }
  ], [t]);

  const pageButtons = useMemo(
    () => buildPaginationArray(page, Number(pagination.total_pages || 0)),
    [page, pagination.total_pages]
  );

  const loadNotifications = React.useCallback(async (category, nextPage, nextPageSize, options = {}) => {
    const silent = Boolean(options.silent);
    if (!silent) {
      setLoading(true);
    }
    setError('');
    try {
      const data = await fetchNotificationsFeed(category, {
        page: nextPage,
        pageSize: nextPageSize
      });
      setNotifications(data.notifications || []);
      setAllowedCategories(data.allowed_categories || []);
      setUnreadCount(Number(data.unread_count || 0));
      setPagination(data.pagination || DEFAULT_PAGINATION);
      setMeta(currentState => ({
        ...currentState,
        ...(data.meta || {})
      }));
    } catch (requestError) {
      console.error('Error loading notifications:', requestError);
      setError(t('NOTIFICATIONS.ERRORS.LOAD'));
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, [t]);

  const loadPushPreferences = React.useCallback(async () => {
    try {
      const data = await fetchPushPreferences();
      setPushPreferences(data.preferences || DEFAULT_PUSH_PREFERENCES);
      setPushCatalog(data.catalog || DEFAULT_PUSH_CATALOG);
    } catch (requestError) {
      console.error('Error loading push preferences:', requestError);
    }
  }, []);

  useEffect(() => {
    loadNotifications(selectedCategory, page, pageSize);
  }, [loadNotifications, page, pageSize, selectedCategory]);

  useEffect(() => {
    loadPushPreferences();
  }, [loadPushPreferences]);

  useEffect(() => {
    let mounted = true;

    const syncInBackground = async () => {
      try {
        await syncNotifications(
          selectedCategory ? { category: selectedCategory } : {}
        );
        if (!mounted) {
          return;
        }
        await loadNotifications(selectedCategory, page, pageSize, { silent: true });
      } catch (requestError) {
        if (requestError?.response?.status !== 429) {
          console.error('Error syncing notifications in background:', requestError);
        }
      }
    };

    syncInBackground();

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    const unsubscribe = subscribeIncomingNotifications(incomingNotifications => {
      if (!incomingNotifications.length) {
        return;
      }

      const hasMatchingCategory = incomingNotifications.some(notification => {
        if (!selectedCategory) return true;
        return String(notification?.category || '').toLowerCase() === String(selectedCategory).toLowerCase();
      });

      if (!hasMatchingCategory) {
        return;
      }

      if (page !== 1) {
        setUnreadCount(currentCount => currentCount + incomingNotifications.filter(notification => notification?.unread !== false).length);
        return;
      }

      setNotifications(currentState => {
        const existingMap = new Map((currentState || []).map(item => [String(item.id), item]));
        incomingNotifications.forEach(item => {
          if (!item || item.id === undefined || item.id === null) {
            return;
          }
          existingMap.set(String(item.id), item);
        });

        const merged = Array.from(existingMap.values()).sort((left, right) => {
          const leftDate = new Date(left?.occurred_at || left?.created_at || 0).getTime();
          const rightDate = new Date(right?.occurred_at || right?.created_at || 0).getTime();
          return rightDate - leftDate;
        });

        return merged.slice(0, pageSize);
      });

      setUnreadCount(currentCount => currentCount + incomingNotifications.filter(notification => notification?.unread !== false).length);
      loadNotifications(selectedCategory, page, pageSize, { silent: true });
    });

    return unsubscribe;
  }, [loadNotifications, page, pageSize, selectedCategory]);

  const handleCategoryChange = category => {
    setSelectedCategory(category);
    setPage(1);
  };

  const handleMarkAllRead = async () => {
    try {
      await markNotificationsRead(selectedCategory ? { category: selectedCategory } : {});
      setNotifications(currentState =>
        currentState.map(notification => ({
          ...notification,
          unread: false
        }))
      );
      setUnreadCount(0);
    } catch (requestError) {
      console.error('Error marking all notifications as read:', requestError);
      setError(t('NOTIFICATIONS.ERRORS.MARK_READ'));
    }
  };

  const savePushPreferences = async payload => {
    setSavingPushPreferences(true);
    try {
      const data = await updatePushPreferences(payload);
      setPushPreferences(data.preferences || DEFAULT_PUSH_PREFERENCES);
      setPushCatalog(data.catalog || DEFAULT_PUSH_CATALOG);
    } catch (requestError) {
      console.error('Error updating push preferences:', requestError);
      setError(t('NOTIFICATIONS.ERRORS.SAVE'));
    } finally {
      setSavingPushPreferences(false);
    }
  };

  const handleCategoryToggle = (category, enabled) => {
    savePushPreferences({
      categories: {
        [category]: Boolean(enabled)
      }
    });
  };

  const handleMercadoLibreTopicToggle = (topicKey, enabled) => {
    savePushPreferences({
      mercadolibre_topics: {
        [topicKey]: Boolean(enabled)
      }
    });
  };

  const handleAgentHeadsupToggle = enabled => {
    savePushPreferences({
      agent_headsup_enabled: Boolean(enabled)
    });
  };

  const handleNotificationClick = async (event, notification) => {
    event.preventDefault();

    setSelectedNotification(notification);
    setShowDetailsModal(true);
    setModalLoading(true);

    try {
      if (notification.unread) {
        await markNotificationsRead({ notification_ids: [notification.id] });
        setNotifications(currentState =>
          currentState.map(item =>
            item.id === notification.id
              ? { ...item, unread: false }
              : item
          )
        );
        setUnreadCount(currentCount => (currentCount > 0 ? currentCount - 1 : 0));
      }

      const destination = notification.destination || resolveNotificationDestination(notification);
      if (shouldAutoNavigate(destination)) {
        window.location.href = destination;
        return;
      }
    } catch (requestError) {
      console.error('Error handling notification click:', requestError);
    } finally {
      setModalLoading(false);
    }
  };

  const closeModal = () => {
    setShowDetailsModal(false);
    setModalLoading(false);
    setSelectedNotification(null);
  };

  return (
    <>
      <PageHeader
        title={t('NOTIFICATIONS.PAGE_TITLE')}
        description={t('NOTIFICATIONS.PAGE_DESCRIPTION')}
        className="mb-3"
      >
        <div className="mt-3 d-flex flex-wrap gap-2 align-items-center">
          <Badge bg="light" text="dark">
            {t('NOTIFICATIONS.UNREAD_COUNT', { count: unreadCount })}
          </Badge>
          <Badge bg="light" text="dark">
            {t('NOTIFICATIONS.META.LAST_SYNC')}: {formatDateTime(meta.last_synced_at, i18n.language, t('NOTIFICATIONS.NEVER'))}
          </Badge>
          <Button variant="outline-primary" size="sm" onClick={handleMarkAllRead}>
            {t('NOTIFICATIONS.ACTIONS.MARK_ALL_READ')}
          </Button>
        </div>
      </PageHeader>

      {error ? <Alert variant="danger">{error}</Alert> : null}

      <Card className="mb-3">
        <Card.Header className="d-flex align-items-center justify-content-between flex-wrap gap-2">
          <span className="fw-semi-bold">{t('NOTIFICATIONS.PUSH_FILTERS.TITLE')}</span>
          {savingPushPreferences ? (
            <span className="text-600 fs--1">
              <Spinner animation="border" size="sm" className="me-2" />
              {t('NOTIFICATIONS.PUSH_FILTERS.SAVING')}
            </span>
          ) : null}
        </Card.Header>
        <Card.Body>
          <Row className="g-3">
            <Col lg={6}>
              <h6 className="mb-2">{t('NOTIFICATIONS.PUSH_FILTERS.BY_CATEGORY')}</h6>
              <div className="d-flex flex-column gap-2">
                {pushCatalog.categories.map(option => (
                  <Form.Check
                    key={option.key}
                    type="switch"
                    id={`push-category-${option.key}`}
                    label={localizePushCategoryLabel(option)}
                    disabled={!option.enabled_by_permission || savingPushPreferences}
                    checked={Boolean((pushPreferences.categories || {})[option.key])}
                    onChange={event => handleCategoryToggle(option.key, event.target.checked)}
                  />
                ))}
                <Form.Check
                  type="switch"
                  id="push-agent-headsup"
                  label={t('NOTIFICATIONS.PUSH_FILTERS.AGENT_HEADSUP')}
                  disabled={savingPushPreferences}
                  checked={Boolean(pushPreferences.agent_headsup_enabled)}
                  onChange={event => handleAgentHeadsupToggle(event.target.checked)}
                />
              </div>
            </Col>
            <Col lg={6}>
              <h6 className="mb-2">{t('NOTIFICATIONS.PUSH_FILTERS.ML_TOPICS')}</h6>
              <div className="d-flex flex-column gap-2" style={{ maxHeight: '22rem', overflowY: 'auto' }}>
                {pushCatalog.mercadolibre_topics.map(topicOption => (
                  <Form.Check
                    key={topicOption.key}
                    type="switch"
                    id={`push-topic-${topicOption.key}`}
                    label={localizePushTopicLabel(topicOption)}
                    disabled={savingPushPreferences}
                    checked={Boolean((pushPreferences.mercadolibre_topics || {})[topicOption.key])}
                    onChange={event => handleMercadoLibreTopicToggle(topicOption.key, event.target.checked)}
                  />
                ))}
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card>
        <Card.Header className="bg-light">
          <Row className="g-2 align-items-center justify-content-between">
            <Col sm={12} lg={8}>
              <div className="d-flex flex-wrap gap-2">
                <Button
                  size="sm"
                  variant={selectedCategory ? 'falcon-default' : 'primary'}
                  onClick={() => handleCategoryChange('')}
                >
                  {t('NOTIFICATIONS.CATEGORIES.ALL')}
                </Button>
                {visibleCategories.map(category => (
                  <Button
                    key={category}
                    size="sm"
                    variant={selectedCategory === category ? 'primary' : 'falcon-default'}
                    onClick={() => handleCategoryChange(category)}
                  >
                    {t(`NOTIFICATIONS.CATEGORIES.${String(category || '').toUpperCase()}`)}
                  </Button>
                ))}
              </div>
            </Col>
            <Col sm={12} lg={4}>
              <div className="d-flex align-items-center justify-content-lg-end gap-2">
                <span className="fs--1 text-600">{t('NOTIFICATIONS.PAGINATION.PAGE_SIZE')}</span>
                <Form.Select
                  size="sm"
                  style={{ width: '6rem' }}
                  value={pageSize}
                  onChange={event => {
                    const nextSize = Number(event.target.value || 20);
                    setPageSize(nextSize);
                    setPage(1);
                  }}
                >
                  {[10, 20, 50].map(option => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </Form.Select>
              </div>
            </Col>
            <Col sm={12}>
              <div className="d-flex flex-wrap gap-2 align-items-center mb-2">
                {statusTabs.map(tab => (
                  <Button
                    key={tab.key}
                    size="sm"
                    variant={statusFilter === tab.key ? tab.variant : `outline-${tab.variant}`}
                    className="rounded-pill px-3"
                    onClick={() => setStatusFilter(tab.key)}
                  >
                    {tab.label}
                    <Badge bg="light" text="dark" className="ms-1 rounded-pill">
                      {Number(statusCounts[tab.key] || 0)}
                    </Badge>
                  </Button>
                ))}
                <Button
                  size="sm"
                  variant="falcon-default"
                  className="rounded-pill px-3"
                  onClick={() => {
                    setSelectedTopics([]);
                    setStatusFilter('all');
                  }}
                >
                  {t('NOTIFICATIONS.FILTERS.CLEAR')}
                </Button>
              </div>
              <div className="d-flex flex-wrap gap-2 align-items-center">
                <span className="fs--1 text-600 me-1">{t('NOTIFICATIONS.FILTERS.ML_TYPES')}</span>
                {topicOptions.map(option => {
                  const selected = selectedTopics.includes(option.key);
                  return (
                    <Button
                      key={option.key}
                      size="sm"
                      className="rounded-pill px-3"
                      variant={selected ? 'primary' : 'outline-primary'}
                      onClick={() => {
                        setSelectedTopics(current => (
                          current.includes(option.key)
                            ? current.filter(key => key !== option.key)
                            : [...current, option.key]
                        ));
                      }}
                    >
                      {option.label}
                      <Badge bg="light" text="dark" className="ms-1 rounded-pill">
                        {Number(topicCounts[option.key] || 0)}
                      </Badge>
                    </Button>
                  );
                })}
              </div>
            </Col>
          </Row>
        </Card.Header>
        <Card.Body className="p-0">
          {loading ? (
            <div className="py-5 text-center">
              <Spinner animation="border" size="sm" className="me-2" />
              {t('NOTIFICATIONS.LOADING')}
            </div>
          ) : filteredNotifications.length ? (
            <>
              {filteredNotifications.map(notification => (
                <Notification
                  key={notification.id}
                  avatar={notification.avatar}
                  unread={notification.unread}
                  time={formatDateTime(notification.occurred_at || notification.created_at, i18n.language)}
                  to="#!"
                  onClick={event => handleNotificationClick(event, notification)}
                >
                  {buildNotificationMarkup(notification)}
                </Notification>
              ))}
              <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 p-3 border-top">
                <span className="text-600 fs--1">
                  {selectedTopics.length || statusFilter !== 'all'
                    ? t('NOTIFICATIONS.FILTERS.MATCHES', {
                        filtered: filteredNotifications.length,
                        total: parsedNotifications.length
                      })
                    : t('NOTIFICATIONS.PAGINATION.SHOWING', {
                        from: Number(pagination.total_items || 0) ? (Number(pagination.page || 1) - 1) * Number(pagination.page_size || pageSize) + 1 : 0,
                        to: Math.min(Number(pagination.page || 1) * Number(pagination.page_size || pageSize), Number(pagination.total_items || 0)),
                        total: Number(pagination.total_items || 0)
                      })}
                </span>
                <div className="d-flex align-items-center gap-2">
                  <Button
                    size="sm"
                    variant="falcon-default"
                    onClick={() => setPage(currentValue => Math.max(1, currentValue - 1))}
                    disabled={!pagination.has_prev}
                  >
                    {t('NOTIFICATIONS.PAGINATION.PREV')}
                  </Button>
                  {pageButtons.map(pageNumber => (
                    <Button
                      key={pageNumber}
                      size="sm"
                      variant={pageNumber === page ? 'primary' : 'falcon-default'}
                      onClick={() => setPage(pageNumber)}
                    >
                      {pageNumber}
                    </Button>
                  ))}
                  <Button
                    size="sm"
                    variant="falcon-default"
                    onClick={() => setPage(currentValue => currentValue + 1)}
                    disabled={!pagination.has_next}
                  >
                    {t('NOTIFICATIONS.PAGINATION.NEXT')}
                  </Button>
                </div>
              </div>
            </>
          ) : (
            <div className="py-5 text-center text-600">{t('NOTIFICATIONS.EMPTY')}</div>
          )}
        </Card.Body>
      </Card>

      <Modal show={showDetailsModal} onHide={closeModal} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{t('NOTIFICATIONS.DETAILS.TITLE')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {modalLoading ? (
            <div className="py-5 text-center">
              <Spinner animation="border" size="sm" className="me-2" />
              {t('NOTIFICATIONS.DETAILS.LOADING')}
            </div>
          ) : selectedNotification ? (
            <div className="d-flex flex-column gap-3">
              <div>
                <div className="fw-bold fs-1">{selectedNotification.displayTitle || selectedNotification.title}</div>
                <div className="text-600 mt-1">{selectedNotification.displayMessage || selectedNotification.message || t('NOTIFICATIONS.NO_DATA')}</div>
              </div>

              <div className="d-flex flex-wrap gap-2">
                <Badge bg="light" text="dark">{selectedNotification.displayTopic || t('NOTIFICATIONS.NO_DATA')}</Badge>
                <Badge bg="light" text="dark">
                  {t(`NOTIFICATIONS.CATEGORIES.${String(selectedNotification.category || '').toUpperCase()}`, { defaultValue: selectedNotification.category || t('NOTIFICATIONS.NO_DATA') })}
                </Badge>
              </div>

              <Row className="g-3 fs--1">
                <Col sm={6}>
                  <div className="text-600">{t('NOTIFICATIONS.DETAILS.AT')}</div>
                  <div className="fw-semi-bold">{formatDateTime(selectedNotification.occurred_at || selectedNotification.created_at, i18n.language, t('NOTIFICATIONS.NEVER'))}</div>
                </Col>
                <Col sm={6}>
                  <div className="text-600">{t('NOTIFICATIONS.DETAILS.TOPIC')}</div>
                  <div className="fw-semi-bold">{selectedNotification.topic || t('NOTIFICATIONS.NO_DATA')}</div>
                </Col>
                <Col sm={12}>
                  <div className="text-600">{t('NOTIFICATIONS.DETAILS.RESOURCE')}</div>
                  <div className="fw-semi-bold text-break">{selectedNotification.resource || t('NOTIFICATIONS.NO_DATA')}</div>
                </Col>
              </Row>

              {Array.isArray(selectedNotification.metadata?.actions) && selectedNotification.metadata.actions.length ? (
                <div>
                  <div className="text-600 fs--2 mb-2">{t('NOTIFICATIONS.DETAILS.ACTIONS')}</div>
                  <div className="d-flex flex-wrap gap-2">
                    {selectedNotification.metadata.actions.map(action => (
                      <Badge bg="light" text="dark" key={String(action)}>{String(action)}</Badge>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="falcon-default" onClick={closeModal}>
            {t('NOTIFICATIONS.DETAILS.CLOSE')}
          </Button>
          {!modalLoading && selectedNotification?.destination ? (
            <Button
              variant="primary"
              onClick={() => {
                const target = selectedNotification.destination;
                closeModal();
                if (target) {
                  window.location.href = target;
                }
              }}
            >
              {t('NOTIFICATIONS.DETAILS.OPEN_LINK')}
            </Button>
          ) : null}
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default Notifications;
